import { DeploymentStatus } from '@kirin/api-client/contracts/organization-members';
import type { OrganizationMember } from '@kirin/api-client/contracts/organization-members';
import type { SecurityGroupMember } from '@kirin/api-client/contracts/security-groups';

/** A group member joined with their organization-level details. */
export interface GroupMemberRow {
  userId: string;
  name: string | null;
  email: string | null;
  /** Highest-precedence role, already display-cased. Empty when unknown. */
  role: string;
  status: GroupMemberStatus;
}

export type GroupMemberStatus = 'Active' | 'Generated' | 'Invited' | 'Unknown';

export const GROUP_MEMBER_SORT_FIELDS = {
  NAME: 'name',
  EMAIL: 'email',
  ROLE: 'role',
  STATUS: 'status',
} as const;

export type GroupMemberSortField =
  (typeof GROUP_MEMBER_SORT_FIELDS)[keyof typeof GROUP_MEMBER_SORT_FIELDS];

/**
 * Role precedence, mirroring the Users page: a member holding several roles is
 * shown by the most privileged one. Lower rank = more privileged.
 */
const ROLE_RANK: Record<string, number> = {
  owner: 0,
  manager: 1,
  member: 2,
  support: 3,
};

const STATUS_RANK: Record<GroupMemberStatus, number> = {
  Active: 0,
  Generated: 1,
  Invited: 2,
  Unknown: 3,
};

function titleCase(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function highestRole(roles: string[] | undefined): string {
  if (!roles?.length) return '';
  const best = [...roles].sort(
    (a, b) =>
      (ROLE_RANK[a.toLowerCase()] ?? 99) - (ROLE_RANK[b.toLowerCase()] ?? 99)
  )[0];
  return titleCase(best);
}

/**
 * Deployment status, mirroring the Users page's precedence: Active (client
 * activity) > Generated (org key created, not yet used) > Invited. An absent
 * value is reported as Unknown rather than silently rendering as Invited — the
 * group page joins against a members list that may not have loaded yet.
 */
function toStatus(member: OrganizationMember | undefined): GroupMemberStatus {
  if (!member) return 'Unknown';
  if (member.deploymentStatus === DeploymentStatus.Active) return 'Active';
  if (member.deploymentStatus === DeploymentStatus.Generated)
    return 'Generated';
  if (member.deploymentStatus === undefined) return 'Unknown';
  return 'Invited';
}

/**
 * Join a group's membership rows (which carry only a Descope user id) with the
 * organization members list to produce displayable rows.
 *
 * A membership can outlive the org-members view of a user (removed from the
 * org, or a list that has not refetched yet). Such a row is KEPT, falling back
 * to the raw id, so the member stays visible and removable rather than silently
 * vanishing from the group.
 */
export function buildGroupMemberRows(
  memberships: SecurityGroupMember[] | undefined,
  orgMembers: OrganizationMember[] | undefined
): GroupMemberRow[] {
  const byId = new Map((orgMembers ?? []).map(member => [member.id, member]));
  return (memberships ?? []).map(membership => {
    const details = byId.get(membership.userId);
    return {
      userId: membership.userId,
      name: details?.name ?? null,
      email: details?.email ?? null,
      role: highestRole(details?.roles),
      status: toStatus(details),
    };
  });
}

/**
 * Sort member rows by a column.
 *
 * Blank name/email/role values sort last in BOTH directions — an absence is not
 * a value, so floating it to the top on a descending sort reads as noise. This
 * matches the Users page's Security Group column and the Created by column.
 */
export function sortGroupMemberRows(
  rows: GroupMemberRow[],
  field: GroupMemberSortField,
  ascending: boolean
): GroupMemberRow[] {
  const direction = ascending ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (field === GROUP_MEMBER_SORT_FIELDS.STATUS) {
      return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * direction;
    }

    const aValue =
      field === GROUP_MEMBER_SORT_FIELDS.NAME
        ? (a.name ?? '')
        : field === GROUP_MEMBER_SORT_FIELDS.EMAIL
          ? (a.email ?? '')
          : a.role;
    const bValue =
      field === GROUP_MEMBER_SORT_FIELDS.NAME
        ? (b.name ?? '')
        : field === GROUP_MEMBER_SORT_FIELDS.EMAIL
          ? (b.email ?? '')
          : b.role;

    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    return aValue.localeCompare(bValue) * direction;
  });
}
